import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Displays "(quantity × unit price)" next to the add-to-cart button label,
 * recalculated live from the quantity selector already on the page.
 * No server round-trip: the unit price (minor units) and the shop's money
 * format/currency are embedded as data attributes at render time, and
 * `@theme/money-formatting` (the same client-side formatter the theme
 * already uses elsewhere, e.g. the price filter section) reproduces the
 * exact separators/currency placement of the Liquid `money` filter.
 *
 * Scoped to the enclosing product-form-component so it only reacts to that
 * form's own quantity selector, never another one on the page (e.g. a
 * different product card's quick-add).
 *
 * @extends {Component}
 */
class AddToCartPriceComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, {
      signal: this.#abortController.signal,
    });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.#update();
  };

  /**
   * @returns {number}
   */
  #getQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    return parseInt(quantityInput?.value ?? '') || 1;
  }

  #update() {
    const unitPrice = Number(this.dataset.unitPrice);
    if (!unitPrice) return;

    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';
    const total = unitPrice * this.#getQuantity();

    this.textContent = `(${formatMoney(total, format, currency)})`;
  }
}

if (!customElements.get('add-to-cart-price')) {
  customElements.define('add-to-cart-price', AddToCartPriceComponent);
}
